import { Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { BlockUI, NgBlockUI }                                 from 'ng-block-ui';
import { TranslateService }                                   from '@ngx-translate/core';

import { CSVService }             from '../core/services/csv.service';
import { ImportOptionsModel }     from './typings/imports.option';
import { ImportsService }         from './imports.service';
import { ImportStructureModel }   from './typings/imports.import-structure';
import { ImportTheirColumnModel } from './typings/imports.their-column';
import { MessagesService }        from '../messages/messages.service';

@Component({
  selector   : 'wor-import',
  template: require('./import.component.html')
})
export class ImportComponent implements OnDestroy {
  @BlockUI('importBlock') block : NgBlockUI;

  @Input() importOptions: ImportOptionsModel;

  @ViewChild('fileInput') fileInput: ElementRef;

  csvArray               : Array<Array<string>> = [];
  errors                 : Array<number>        = [];
  fileName               : string;
  importStructure        : Array<ImportStructureModel>;
  loading                : boolean;
  ourTransformedColumns  : Array<any>;
  rowHidden              : Array<boolean>;
  selectColumnVisible    : boolean;
  selectedFile           : File;
  theirColumns           : Array<ImportTheirColumnModel>;
  theirTransformedColumns: Array<ImportTheirColumnModel>;

  hasHeaders = true;

  constructor (
    protected csvService      : CSVService,
    protected importsService  : ImportsService,
    protected messagesService : MessagesService,
    protected translateService: TranslateService
  ) {}

  addColumn ( columnName : string ) : void {
    const selected = this.theirColumns.find(column => column.name === columnName);

    this.theirTransformedColumns.push({
      name  : selected.name,
      values: [ ...selected.values ]
    });

    this.importStructure.push({
      action    : '',
      ours      : '',
      theirs    : selected.index,
      user_input: []
    });

    this.ourTransformedColumns.push(null);
    this.rowHidden.push(false);

    this.selectColumnVisible = false;
  }

  analyze () : void {
    this.block.start();

    this.loading = true;

    this.csvService.parse(this.selectedFile)
    .then(( csvArray : Array<Array<string>> ) => {
      this.csvArray = csvArray;

      this.buildGrid();
    })
    .catch(error => {
      this.messagesService.error(error);

      this.fileInput.nativeElement.value = '';
    })
    .finally(() => {
      this.block.stop();

      this.loading = false;
  });
  }

  buildGrid () : void {
    if ( !this.csvArray?.length ) {
      return;
    }

    this.importStructure         = [];
    this.ourTransformedColumns   = [];
    this.rowHidden               = [];
    this.theirColumns            = [];
    this.theirTransformedColumns = [];

    // Now lets loop through the csv array and manipulate each array inside of it
    for ( let i = 0; i < this.csvArray[ 0 ].length ; i++ ) {
      // If first row contains column names
      if ( this.hasHeaders ) {

        // Each column gets an object in our import structure with empty data except
        // 'theirs'. Their column names don't matter on the backend so just specify the
        // index in the row
        this.importStructure.push({
          action    : 'ignore_column',
          ours      : '',
          theirs    : i,
          user_input: []
        });

        // BUT their column needs to display the name so do the same thing as the
        // import structure but display the name instead of the index
        this.theirTransformedColumns.push({
          name  : this.csvArray[ 0 ][ i ],
          values: []
        });

        // Build a list of column names for the new column dropdown
        this.theirColumns.push({
          index : i,
          name  : this.csvArray[ 0 ][ i ],
          values: []
        });

        let x = 1;

        // No we need to loop through the csv array to get some example data.
        // Get the first four records after the column names or all the records,
        // whichever comes first
        while ( x <= 4 && x < this.csvArray.length ) {
          this.theirTransformedColumns[ i ].values.push(this.csvArray[ x ][ i ]);

          this.theirColumns[ i ].values.push(this.csvArray[ x ][ i ]);

          x++;
        }
      }
      // If first row doesn't contain column names
      else {
        // Same as above, except we create a column row, with fake names to display
        this.importStructure.push({
          theirs    : i,
          ours      : '',
          action    : 'ignore_column',
          user_input: []
        });

        this.theirTransformedColumns.push({
          name  : `Column${ i + 1 }`,
          values: []
        });

        this.theirColumns.push({
          index : i,
          name  : `Column${ i + 1 }`,
          values: []
        });

        let x = 0;

        // This is the same too except we can start at 0 since their are no
        // column names to worry about
        while ( x < 4 && x < this.csvArray.length ) {
          this.theirTransformedColumns[ i ].values.push(this.csvArray[ x ][ i ]);

          this.theirColumns[ i ].values.push(this.csvArray[ x ][ i ]);

          x++;
        }
      }

      this.rowHidden.push(false);
    }
  }

  chooseColumn () : void {
    this.selectColumnVisible = true;
  }

  ignore ( index : number ) : void {
    this.importStructure[ index ].action = 'ignore_column';
    this.rowHidden[ index ]              = true;
  }

  import () : void {
    this.errors = [];

    for ( let i = 0; i < this.importStructure.length; i++ ) {
      const row = this.importStructure[ i ];

      if ( row.action && row.action !== 'ignore_column' && !row.ours ) {
        this.errors.push(i);
      }
    }

    if ( this.errors.length ) {
      this.messagesService.show(this.translateService.instant('JS_SPACE.IMPORTS.SELECT_OR_SPECIFY_COLUMNS'), 'error');

      return;
    }

    this.importsService.start(this.importOptions.url, this.selectedFile, this.importStructure, this.hasHeaders)
    .then(( message : string ) => this.messagesService.show(message, 'success'))
    .catch(error => this.messagesService.error(error));
  }

  moveToStructure ( index : number, columnName : string ) : void {
    const selected = this.importOptions.ourColumns.find(column => column.name === columnName);

    this.ourTransformedColumns[ index ] = selected;

    this.errors = this.errors.filter(error => error !== index);

    if ( !selected ) {
      return;
    }

    this.importStructure[ index ].ours = selected.name;

    if ( this.importStructure[ index ].action === 'ignore_column' ) {
      if ( this.importsService.isPhoneColumn(selected.name) ) {
        this.importStructure[ index ].action = 'extract_numeric';
      }
      else {
        this.importStructure[ index ].action = 'leave_as_is';
      }
    }
  }

  ngOnDestroy () : void {
    this.fileInput.nativeElement.value = '';
  }

  onActionChange ( index : number ) : void {
    this.importStructure[ index ].user_input = [];
  }

  onFileSelect ( event : Event ) : void {
    const input = event.target as HTMLInputElement;

    if ( input.files && input.files.length > 0 ) {
      const file = input.files[ 0 ];

      if ( file ) {
        this.selectedFile = file;
        this.fileName     = this.selectedFile.name;
      }
      else {
        this.selectedFile = null;
        this.fileName     = null;
        input.value       = '';
      }
    }
  }

  selectedEquation ( index : number ) : any {
    const action = this.importStructure[ index ]?.action;

    if ( !action ) {
      return null;
    }

    return this.importOptions.transformFunctions.find(f => f.action === action) || null;
  }

  toggleBox () : void {
    this.importOptions.collapsed = !this.importOptions.collapsed;
  }
}
